// Set-operator branch scoping.
//
// A compound SELECT (branches separated by top-level UNION / INTERSECT / EXCEPT,
// and Oracle-only MINUS) has PER-BRANCH alias scope: an alias declared in one
// branch's FROM list must not resolve with the cursor in another branch. This
// module returns the byte range of the branch that owns the cursor so
// resolveScope can scan aliases branch-locally instead of over the whole
// statement.
//
// Set-operator membership (INCLUDING the Oracle-only MINUS dialect gate) is
// derived entirely from `splitSelectClauses` — the SAME source the structure
// extractor (extractQueryStructure.select) uses to split branches — so this
// scoper can never drift from Plan 5's set-op parser. It hard-codes no keyword
// list of its own.
//
// Carve-outs that keep whole-statement visibility (return null):
//   • non-compound statements (a single branch);
//   • the WITH-clause header before the first branch's SELECT (belongs to no
//     branch — a leading WITH is shared by every branch);
//   • a cursor on a set-operator separator between two branches;
//   • decision 2 — the trailing ORDER BY / LIMIT of the last branch, which is
//     owned by the whole compound and legally references output columns from
//     every branch (restricting it would under-suggest).
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver.

import type { ParseOptions } from '../../sql-grammar/model';
import { splitSelectClauses, type SelectClauseRanges } from '../structure/selectShape';

export interface BranchRange {
  readonly start: number;
  readonly end: number;
}

interface BranchSpan {
  readonly start: number;
  readonly end: number;               // exclusive: set-op keyword start, or stmtEnd
  readonly sepStart: number | null;   // set-op keyword start ending this branch (null for last)
  readonly nextStart: number;         // start of the next branch (past set-op + ALL/DISTINCT)
  readonly tailStart: number | null;  // last-branch trailing ORDER BY/LIMIT start
}

export function setOpBranchRange(
  source: string,
  stmtStart: number,
  stmtEnd: number,
  offset: number,
  options: ParseOptions,
): BranchRange | null {
  const first = splitSelectClauses(source, stmtStart, stmtEnd, options);
  if (!first || !first.setOpKw) return null;      // not a compound → whole statement
  if (offset < first.selectKw.start) return null; // WITH header / pre-SELECT → no branch

  const spans = collectSpans(source, stmtStart, stmtEnd, options, first);
  const lastIdx = spans.length - 1;
  for (let k = 0; k <= lastIdx; k++) {
    const b = spans[k];
    const inSpan = k === lastIdx
      ? offset >= b.start && offset <= b.end   // last branch owns the trailing EOF
      : offset >= b.start && offset < b.end;
    if (inSpan) {
      // decision 2 carve-out: trailing ORDER BY / LIMIT of the last branch is
      // the whole compound's tail → keep whole-statement visibility.
      if (k === lastIdx && b.tailStart !== null && offset >= b.tailStart) return null;
      return { start: b.start, end: b.end };
    }
    // Cursor sits on the set-op separator between this branch and the next.
    if (b.sepStart !== null && offset >= b.end && offset < b.nextStart) return null;
  }
  return null;
}

function collectSpans(
  source: string,
  stmtStart: number,
  stmtEnd: number,
  options: ParseOptions,
  first: SelectClauseRanges,
): BranchSpan[] {
  const out: BranchSpan[] = [];
  let clauses: SelectClauseRanges | null = first;
  let i = stmtStart;
  while (clauses) {
    const sepStart = clauses.setOpKw ? clauses.setOpKw.start : null;
    const end = sepStart ?? stmtEnd;
    let nextStart = stmtEnd;
    if (clauses.setOpKw) nextStart = afterSeparator(source, clauses.setOpKw.end, stmtEnd);
    const tailStart = clauses.setOpKw ? null : tailStartOf(clauses);
    out.push({ start: i, end, sepStart, nextStart, tailStart });
    if (!clauses.setOpKw) break;
    i = nextStart;
    clauses = splitSelectClauses(source, i, stmtEnd, options);
  }
  return out;
}

// Advance past the set-op keyword's optional ALL / DISTINCT modifier — mirrors
// extractQueryStructure.select's branch walk so separator boundaries agree.
function afterSeparator(source: string, keywordEnd: number, end: number): number {
  let n = keywordEnd;
  while (n < end && /\s/.test(source[n])) n++;
  const m = source.slice(n, end).match(/^(?:ALL|DISTINCT)\b/i);
  return m ? n + m[0].length : n;
}

function tailStartOf(c: SelectClauseRanges): number | null {
  const candidates: number[] = [];
  if (c.orderKw) candidates.push(c.orderKw.start);
  if (c.limitKw) candidates.push(c.limitKw.start);
  return candidates.length ? Math.min(...candidates) : null;
}
